import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GeneralChat, PrivateChat } from './components/Chat';
import { ErrorPage } from './components/Error';
import { FinalTest } from './components/FinalTest';
import { Header } from './components/Header';
import { Interactive } from './components/Interactive';
import { Main } from './components/Main';
import { SimpleModal } from './components/modals';
import { Sidebar } from './components/Sidebar';
import { Listeners, Participants } from './components/Sidebar/components';
import { RoutPageS } from './components/Sidebar/styles';
import { GlobalStyle } from './GlobalStyles';
import { useClickStat } from './hooks/useClickStat';
import { useSocket } from './hooks/useSocket';
import { AppStore } from './store/applicationState';
import { GetCerteficate } from './store/certeficate/actions';
import { GetFinalTest } from './store/finalTest/actions';
import { InteractiveActions } from './store/interactive/actions';
import { setSideBar } from './store/sidebar/actions';
import { MetricsUkd, ShowEvent } from './store/sockets/actions';
import { GetTicket } from './store/ticket/actions';
import { CheckUser, UsersActions } from './store/users/actions';
import { setVideo } from './store/video/actions';
import { GROUP_SEMINAR } from './utils/consts';
import { EIcons } from './utils/globatTypes';
import generateID2 from './utils/id2';
import { messageErrors } from './utils/messageErrors';
import writtenTimeFormat from './utils/writtenTimeFormat';
import { GetTechnicalTexts } from './store/technicalTexts/actions';

function App() {
  const {
    Sidebar: RSidebar,
    Users: RUsers,
    Interactive: RInteractive,
    Sockets,
    FinalTest: RFinalTest,
    Certeficate,
    TechnicalTexts,
  } = useSelector((store: AppStore) => store);
  const [sidebarVisible, setSidebarVisible] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { joinToRoom, leaveRoom } = useSocket();
  const dispatch = useDispatch();

  useClickStat();

  React.useEffect(() => {
    if (typeof RUsers.isAuth !== 'undefined' && !RUsers.isAuth) {
      dispatch(UsersActions.setError(TechnicalTexts.data?.text_unauthorized || messageErrors.messageAuth));
    }
  }, [dispatch, TechnicalTexts.data, RUsers.isAuth]);

  React.useEffect(() => {
    if (typeof RUsers.isAccess !== 'undefined' && !RUsers.isAccess) {
      dispatch(UsersActions.setError(TechnicalTexts.data?.text_unaccess || messageErrors.messageAccess));
    }
  }, [dispatch, TechnicalTexts.data, RUsers.isAccess]);

  React.useEffect(() => {
    if (RInteractive.redirect) {
      dispatch(setSideBar(RInteractive.redirect));
      dispatch(InteractiveActions.setRedirect(null));
    }
  }, [dispatch, RInteractive]);

  React.useEffect(() => {
    generateID2((token: string | null) => {
      const pathname = window.location.pathname.replace('/client/', '');
      const id = Number(pathname.split('-')[0]);
      if (token && id) {
        dispatch(GetTechnicalTexts.request({ data: { event_id: id } }));
        dispatch(CheckUser.request({ data: { event_id: id } }));
      } else if (id) {
        dispatch(
          GetTechnicalTexts.request({
            data: { event_id: id },
            callBack: () => {
              dispatch(UsersActions.setIsAuth(false));
            },
          })
        );
      }
    });
  }, [dispatch]);

  React.useEffect(() => {
    const pathname = window.location.pathname.replace('/client/', '');
    const id = Number(pathname.split('-')[0]);
    if (RUsers.user && id) {
      dispatch(ShowEvent.request({ data: { event_id: id } }));
      dispatch(MetricsUkd.request({ data: { event_id: id } }));
      dispatch(GetFinalTest.request({ event_id: id }));
      dispatch(GetCerteficate.request({ data: { event_id: id } }));
    }
  }, [dispatch, RUsers.user]);

  React.useEffect(() => {
    if (Sockets.event && Sockets.event?.typeEvent === 2)
      dispatch(
        GetTicket.request({
          event_id: Sockets.event.absnum,
        })
      );
  }, [dispatch, Sockets.event]);

  React.useEffect(() => {
    if (RSidebar.name === EIcons.chat || RSidebar.name === EIcons.listeners || RSidebar.name === EIcons.group) {
      setSidebarVisible(true);
    } else {
      setSidebarVisible(false);
    }
    if (RSidebar.name === EIcons.interactive || RSidebar.name === EIcons.final) {
      dispatch(setVideo(false));
    } else {
      dispatch(setVideo(true));
    }
  }, [RSidebar.name, dispatch]);

  React.useEffect(() => {
    if (Sockets.event?.group_id === GROUP_SEMINAR.MED && Certeficate.data?.certeficate) {
      if (RFinalTest.data?.endDate && !Certeficate.data.certeficate.is_done && new Date(RFinalTest.data?.endDate) > new Date()) {
        setOpen(true);
      }
    }
    // eslint-disable-next-line
  }, [Sockets.event?.group_id, RFinalTest.data?.endDate, Certeficate.data?.certeficate?.is_done]);

  const renderRoutePage = () => {
    if (RSidebar.name === EIcons.group) {
      return <Participants />;
    }
    if (RSidebar.name === EIcons.chat) {
      return <PrivateChat />;
    }
    if (RSidebar.name === EIcons.listeners) {
      return <Listeners />;
    }
  };

  const renderBody = () => {
    if (Sockets.event?.type_id === 3 && Sockets.event?.is_register === 1 && !RUsers.user?.isRegister) {
      return (
        <ErrorPage
          message={
            TechnicalTexts.data?.text_unregistered
              ?.split(' ')
              .map((item) =>
                item.includes('зареєстру') ? `<a href="${window.location.origin}/${Sockets.event?.cleanurl}" target="_blank">${item}</a>` : item
              )
              .join(' ') || messageErrors.messageRegisterVebinar(Sockets.event.cleanurl)
          }
        />
      );
    }
    if (!RUsers.user || RUsers.error) {
      return <ErrorPage message={RUsers.error} />;
    }
    return (
      <>
        <div
          style={{
            flex: 1,
            margin: '10px',
            position: 'relative',
          }}
        >
          <Main />
          {RSidebar.name === EIcons.interactive ? <Interactive /> : null}
          {RSidebar.name === EIcons.final ? <FinalTest /> : null}
        </div>
        <GeneralChat />
      </>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <GlobalStyle />
      <Sidebar />
      <div style={{ flex: 1 }}>
        <Header joinToRoom={joinToRoom} leaveRoom={leaveRoom} />
        <main>
          {sidebarVisible ? <RoutPageS>{renderRoutePage()}</RoutPageS> : null}
          {renderBody()}
        </main>
      </div>
      {open && RFinalTest.data ? (
        <SimpleModal
          text={`
        Через ${writtenTimeFormat(
          new Date(RFinalTest.data.endDate).getTime()
        )} доступ до фінального тесту закриється. Встигніть пройти тестування, щоб отримати сертифікат!
      `}
          closeHandler={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default App;
